import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageIcon } from '@c2fo/liquidity/icons';
import useUpdateUserLanguage from '@/enterprise/data/useUpdateUserLanguage';
import { getLanguage, SUPPORTED_LOCALES } from '@/i18n';
import { useReporting } from '@/reporting';
import DrawerSubmenuOverlay from './DrawerSubmenuOverlay';
import SubmenuDropdownItem from './SubmenuDropdownItem';

interface SubmenuOverlayProps {
  isOpen: boolean;
  onBack: () => void;
  onClose: () => void;
  portalContainer: Element | null;
  shouldShowPortal: boolean;
}

interface LanguageMenuItem {
  label: string;
  name: string;
  key: string;
  isSelected: boolean;
  onClick: () => void;
}

interface UseLanguageMenuOptions {
  onSelect?: () => void;
}

const useLanguageMenu = (options?: UseLanguageMenuOptions) => {
  const { i18n } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { mutate: mutateLanguage } = useUpdateUserLanguage();
  const language = useMemo(() => getLanguage(i18n.language), [i18n.language]);

  const handleLanguageChange = useCallback(
    (localeCode: string) => {
      i18n.changeLanguage(localeCode);
      // do not want this to block updating language
      // if it fails, we fallback to default behavior for language
      mutateLanguage({ locale: localeCode });
      trackEnterpriseEvent('language::changed', { language: localeCode });
      options?.onSelect?.();
    },
    [i18n, mutateLanguage, options, trackEnterpriseEvent]
  );

  const items: LanguageMenuItem[] = useMemo(() => {
    return SUPPORTED_LOCALES.map((locale) => ({
      name: locale.code,
      label: locale.language,
      key: locale.code,
      isSelected: language === locale.language,
      onClick: () => handleLanguageChange(locale.code),
    }));
  }, [handleLanguageChange, language]);

  return {
    language,
    items,
    handleLanguageChange,
  };
};

export const LanguageMenuItem = () => {
  const { t } = useTranslation();
  const { language, items } = useLanguageMenu();

  return (
    <SubmenuDropdownItem
      icon={LanguageIcon}
      items={items}
      selectedValue={language}
      title={t('core.preferredLanguage')}
      width="w-48"
    />
  );
};

export const LanguageSubmenuOverlay = ({
  isOpen,
  onBack,
  onClose,
  portalContainer,
  shouldShowPortal,
}: SubmenuOverlayProps) => {
  const { t } = useTranslation();
  const { items } = useLanguageMenu({ onSelect: onClose });

  return (
    <DrawerSubmenuOverlay
      items={items}
      isOpen={isOpen}
      onBack={onBack}
      portalContainer={portalContainer}
      shouldShowPortal={shouldShowPortal}
      title={t('core.preferredLanguage')}
    />
  );
};
